import {
  Assessment,
  AutoFixHigh,
  CropOriginal,
  Help,
  MovieCreation,
  PersonAdd,
  QueuePlayNext,
  Router,
} from '@mui/icons-material';
import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { To } from 'react-router-dom';
import { Link } from '~/components/link';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { supportEmail } from '~/lib/constants';

interface QuickLinkProps {
  icon: ReactNode;
  title: string;
  caption: string;
  to?: To;
  href?: string;
}

const QuickLink = ({ icon, title, caption, to, href }: QuickLinkProps) => {
  const theme = useTheme();

  const content = (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ color: theme.palette.primary.main, mb: 1 }}>{icon}</Box>
      <Typography component="h2" variant="h4" fontWeight={600} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {caption}
      </Typography>
    </Paper>
  );

  if (to) {
    return (
      <Link to={to} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
        {content}
      </a>
    );
  }

  return content;
};

export const QuickLinks = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const link = useLink();

  const canAccessDevices = currentNetwork.canAccessDevices.value;
  const canAccessStudio = currentNetwork.canAccessStudio.value;
  const canAccessShows = currentNetwork.canAccessShows.value;
  const canAccessContent = currentNetwork.canAccessContent.value;
  const canAccessReporting = currentNetwork.canAccessReporting.value;
  const canManage = currentNetwork.canManage.value;
  const canActivateDevices = currentNetwork.canActivateDevices.value;

  const links: QuickLinkProps[] = [];

  // Device-related links
  if (canAccessDevices) {
    links.push({
      icon: <Router sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.monitor_devices.title'),
      caption: t('pages.dashboard.shortcuts.monitor_devices.caption'),
      to: link('/devices'),
    });
  }

  // Studio/Content links
  if (canAccessStudio || canAccessContent) {
    links.push({
      icon: <CropOriginal sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.manage_content.title'),
      caption: t('pages.dashboard.shortcuts.manage_content.caption'),
      to: link('/content'),
    });
  }

  // Shows link
  if (canAccessShows) {
    links.push({
      icon: <MovieCreation sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.manage_shows.title'),
      caption: t('pages.dashboard.shortcuts.manage_shows.caption'),
      to: link('/shows'),
    });
  }

  // Studio link
  if (canAccessStudio) {
    links.push({
      icon: <AutoFixHigh sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.studio.title'),
      caption: t('pages.dashboard.shortcuts.studio.caption'),
      to: link('/studio'),
    });
  }

  // Reporting link
  if (canAccessReporting) {
    links.push({
      icon: <Assessment sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.view_reports.title'),
      caption: t('pages.dashboard.shortcuts.view_reports.caption'),
      to: link('/playout-reports'),
    });
  }

  // Management links
  if (canManage) {
    links.push({
      icon: <PersonAdd sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.create_user.title'),
      caption: t('pages.dashboard.shortcuts.create_user.caption'),
      to: link('/users?new=true'),
    });
  }

  if (canActivateDevices) {
    links.push({
      icon: <QueuePlayNext sx={{ fontSize: 56 }} />,
      title: t('pages.dashboard.shortcuts.activate_device.title'),
      caption: t('pages.dashboard.shortcuts.activate_device.caption'),
      to: link('/devices/activate'),
    });
  }

  // Help link - always shown
  links.push({
    icon: <Help sx={{ fontSize: 56 }} />,
    title: t('pages.dashboard.shortcuts.need_help.title'),
    caption: t('pages.dashboard.shortcuts.need_help.caption'),
    href: `mailto:${supportEmail}`,
  });

  return (
    <Grid container spacing={2}>
      {links.map((linkProps, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <QuickLink {...linkProps} />
        </Grid>
      ))}
    </Grid>
  );
};
